import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Layers, Heart, Eye, Compass, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';

const Metodologia = () => {
  const principles = [
    {
      icon: Heart,
      title: 'Experiencia real',
      description: 'Todo lo que comparto nace de la experiencia vivida, no de la teoría. Cada herramienta ha sido integrada y probada antes de ser transmitida.'
    },
    {
      icon: Layers,
      title: 'Estructura y sensibilidad',
      description: 'Integro la precisión estructural con la apertura sensitiva. No son opuestos, sino complementos que se potencian mutuamente.'
    },
    {
      icon: Eye,
      title: 'Consciencia habitada',
      description: 'Trabajamos para sentirnos seguros dentro del cuerpo, no para escapar de él. La espiritualidad se vive en lo cotidiano.'
    },
    {
      icon: Compass,
      title: 'Autonomía personal',
      description: 'Mi intención no es crear dependencia, sino que confíes cada vez más en ti. Yo acompaño, tú eres quien camina.'
    }
  ];

  return (
    <div className="pt-20">
      {/* Hero */}
      <section className="py-16 sm:py-20 bg-gradient-to-b from-white to-[#faf8f5]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-[#c9a227]/10 rounded-full px-4 py-2 mb-6">
              <Sparkles className="w-4 h-4 text-[#c9a227]" />
              <span className="text-sm text-[#c9a227] font-medium">Escuela de Consciencia Akáshica</span>
            </div>
            
            <h1 
              className="text-3xl sm:text-4xl lg:text-5xl font-heading text-[hsl(var(--text-primary))] mb-6 leading-tight"
              data-testid="metodologia-title"
            >
              Metodología
            </h1>
            <p className="text-lg text-[hsl(var(--text-secondary))] leading-relaxed max-w-2xl mx-auto">
              Una forma de acompañar desde la claridad, la cercanía, la alegría y el humor consciente.
              Porque la verdad no tiene por qué ser pesada.
            </p>
          </div>
        </div>
      </section>

      {/* Principles */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-12">
            <div className="w-12 h-[2px] bg-[#c9a227] mx-auto mb-6" />
            <h2 className="text-2xl sm:text-3xl font-heading text-[hsl(var(--text-primary))]">
              Principios fundamentales
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {principles.map((principle, index) => (
              <div 
                key={index}
                className="card-soft hover-lift"
                data-testid={`principle-${index}`}
              >
                <div className="flex gap-6">
                  <div className="w-14 h-14 rounded-full bg-[hsl(var(--accent))]/10 flex items-center justify-center flex-shrink-0">
                    <principle.icon className="w-6 h-6 text-[hsl(var(--accent))]" strokeWidth={1.5} />
                  </div>
                  <div>
                    <h3 className="text-xl font-heading text-[hsl(var(--text-primary))] mb-3">
                      {principle.title}
                    </h3>
                    <p className="text-[hsl(var(--text-secondary))] leading-relaxed">
                      {principle.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-16 sm:py-20 bg-[#faf8f5]">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-12">
            <div className="w-12 h-[2px] bg-[#c9a227] mx-auto mb-6" />
            <h2 className="text-2xl sm:text-3xl font-heading text-[hsl(var(--text-primary))]">
              Cómo funciona
            </h2>
          </div>

          <div className="space-y-12">
            <div className="flex gap-8 items-start">
              <div className="w-12 h-12 rounded-full bg-[hsl(var(--accent))] flex items-center justify-center flex-shrink-0 text-white font-heading">
                1
              </div>
              <div>
                <h3 className="text-xl font-heading text-[hsl(var(--text-primary))] mb-3">
                  Fundamentos claros
                </h3>
                <p className="text-[hsl(var(--text-secondary))] leading-relaxed">
                  Cada formación comienza con bases sólidas. Entender el "porqué" y el "cómo" 
                  antes de practicar. Sin misterios innecesarios, con claridad y profundidad.
                </p>
              </div>
            </div>

            <div className="flex gap-8 items-start">
              <div className="w-12 h-12 rounded-full bg-[hsl(var(--sage))] flex items-center justify-center flex-shrink-0 text-white font-heading">
                2
              </div>
              <div>
                <h3 className="text-xl font-heading text-[hsl(var(--text-primary))] mb-3">
                  Práctica guiada
                </h3>
                <p className="text-[hsl(var(--text-secondary))] leading-relaxed">
                  Aprender haciendo, con acompañamiento cercano. Cada técnica se practica 
                  paso a paso, integrando la experiencia en tu vida cotidiana.
                </p>
              </div>
            </div>

            <div className="flex gap-8 items-start">
              <div className="w-12 h-12 rounded-full bg-[hsl(var(--accent))] flex items-center justify-center flex-shrink-0 text-white font-heading">
                3
              </div>
              <div>
                <h3 className="text-xl font-heading text-[hsl(var(--text-primary))] mb-3">
                  Integración consciente
                </h3>
                <p className="text-[hsl(var(--text-secondary))] leading-relaxed">
                  No se trata de acumular información, sino de encarnarla. Cada aprendizaje 
                  se asienta a tu ritmo, sin prisas, respetando tu proceso único.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quote */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-3xl mx-auto px-6 lg:px-12 text-center">
          <div className="bg-gradient-to-br from-[#faf8f5] to-white rounded-3xl p-10 lg:p-12 shadow-sm">
            <div className="w-12 h-12 rounded-full bg-[#c9a227]/10 flex items-center justify-center mx-auto mb-6">
              <Sparkles className="w-6 h-6 text-[#c9a227]" />
            </div>
            <blockquote className="font-heading text-xl lg:text-2xl text-[hsl(var(--text-primary))] leading-relaxed">
              "Este espacio no existe para sacarte de tu vida. Existe para ayudarte a habitarla 
              con más presencia, claridad y calma."
            </blockquote>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 sm:py-20 bg-[#faf8f5]">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <h2 className="text-2xl sm:text-3xl font-heading text-[hsl(var(--text-primary))] mb-4">
            ¿Resuena contigo?
          </h2>
          <p className="text-[hsl(var(--text-secondary))] mb-8 max-w-lg mx-auto">
            Explora las formaciones disponibles y comienza tu camino hacia una 
            consciencia más habitada, clara y autónoma.
          </p>
          <Link to="/formaciones" data-testid="metodologia-cta">
            <Button className="bg-[#c9a227] hover:bg-[#b89220] text-white px-8 py-6 rounded-full font-medium shadow-lg hover:shadow-xl transition-all">
              Ver formaciones
              <ArrowRight className="w-4 h-4 ml-2" strokeWidth={1.5} />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Metodologia;
